import React, {
  useState, useEffect, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import {
  buildExportFilename, exportRowsToCSV, exportTableToFile,
} from '../../../shared/utils/tableExport';
import ManageCoachesTable from './presentational/tables/ManageCoachesTable';
import AddCoachModal from './presentational/modals/AddCoachModal';
import ExportDataModal from './presentational/modals/ExportDataModal';
import ConfirmModal from './presentational/modals/ConfirmModal';
import { fetchCoaches, deleteUsers, resendActivationEmail } from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #F7F7F7;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const HiddenTable = styled.table`
  display: none;
`;

const ManageCoaches = ({ backPath, canManageCoaches }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const coaches = useSelector((state) => state.navigator.data.coaches);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const allCalendars = useSelector((state) => state.calendars.data.unparsedCalendars);
  const groups = useSelector((state) => state.navigator.data.groups);
  const isDeletingUsers = useSelector((state) => state.navigator.ui.isDeletingUsers);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );

  const [isAddCoachOpen, setIsAddCoachOpen] = useState(false);
  const [coachToEdit, setCoachToEdit] = useState(null);
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [coachToResend, setCoachToResend] = useState(null);
  const [isSendingActivation, setIsSendingActivation] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [visibleCoaches, setVisibleCoaches] = useState(coaches);

  const resendRef = useRef(null);
  if (coachToResend) resendRef.current = coachToResend;
  const displayResend = resendRef.current;

  const deleteRef = useRef(null);
  if (coachToDelete) deleteRef.current = coachToDelete;
  const displayDelete = deleteRef.current;

  useEffect(() => {
    if (accountCode) {
      dispatch(fetchCoaches(accountCode));
    }
  }, [accountCode, dispatch]);

  const refetchCoaches = () => dispatch(fetchCoaches(accountCode));

  const calendarMap = calendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {});
  const groupMap = groups.reduce((acc, g) => { acc[g.id] = g; return acc; }, {});
  // Fallback so an archived calendar still resolves to a real name in the export.
  const allCalendarsMap = allCalendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {});

  const getCoachCalendarNames = (c) => {
    if (c.calAccess === null) return 'All Calendars';
    if (Array.isArray(c.calAccess) && c.calAccess.length > 0) {
      return c.calAccess.map((id) => {
        const cal = calendarMap[id] || allCalendarsMap[id];
        if (!cal) return null;
        return cal.archived ? `${cal.name} (Archived)` : cal.name;
      }).filter(Boolean).join('; ');
    }
    return '';
  };

  const getCoachGroupNames = (c) => {
    if (c.userGroupIds === null) return 'All Groups';
    if (Array.isArray(c.userGroupIds) && c.userGroupIds.length > 0) {
      return c.userGroupIds.map((id) => groupMap[id]?.name).filter(Boolean).join('; ');
    }
    return '';
  };

  const getCoachLastActivity = (c) => (c.activated === 1 ? (c.lastActivity || '') : 'Invited');

  const exportToXLSX = () => {
    exportTableToFile('coaches-export-table', buildExportFilename('coaches', 'xlsx'), 'Coaches');
    setIsExportOpen(false);
  };

  const exportToXLS = () => {
    exportTableToFile('coaches-export-table', buildExportFilename('coaches', 'xls'), 'Coaches');
    setIsExportOpen(false);
  };

  const exportToCSV = () => {
    const headers = [
      'First', 'Last', usernamesEnabled ? 'Username' : 'Email', 'Groups', 'Calendar', 'Last Activity',
    ];
    const rows = visibleCoaches.map((c) => [
      c.first || '',
      c.last || '',
      (usernamesEnabled ? c.username : c.email) || '',
      getCoachGroupNames(c),
      getCoachCalendarNames(c),
      getCoachLastActivity(c),
    ]);
    exportRowsToCSV(headers, rows, buildExportFilename('coaches', 'csv'));
    setIsExportOpen(false);
  };

  const handleBulkAction = (action, ids) => {
    setBulkAction(action);
    setBulkSelectedIds(ids);
  };

  const confirmBulkDelete = () => {
    dispatch(deleteUsers(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} coach${bulkSelectedIds.length === 1 ? '' : 'es'} deleted.`);
        refetchCoaches();
        setBulkAction(null);
        setBulkSelectedIds([]);
      },
    }));
  };

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <BackLink type='button' onClick={() => history.push(backPath)}>
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Manage
          </BackLink>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Manage Coaches
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Export'
            icon='download'
            iconColor='#1A1A1A'
            customBorderRadius='4px'
            borderColor='#D6D6D6'
            txtColor='#1A1A1A'
            bgColor='var(--theme-bg-surface, #FFF)'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsExportOpen(true)}
          />
          {canManageCoaches && (
            <Button
              name='Add Coaches'
              icon='add-user'
              iconColor='#FFF'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='4px'
              styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
              styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
              onClick={() => setIsAddCoachOpen(true)}
            />
          )}
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <ManageCoachesTable
          onEditCoach={setCoachToEdit}
          onDeleteCoach={setCoachToDelete}
          onResendActivation={setCoachToResend}
          onBulkAction={handleBulkAction}
          onVisibleCoachesChange={setVisibleCoaches}
          canManageCoaches={canManageCoaches}
        />
      </BottomSection>

      {/* Resend Activation Modal */}
      <ConfirmModal
        isOpen={!!coachToResend}
        onClose={() => setCoachToResend(null)}
        onConfirm={() => {
          if (usernamesEnabled) return;
          setIsSendingActivation(true);
          dispatch(resendActivationEmail(accountCode, coachToResend.id, {
            onSuccess: () => {
              showSuccessToast('Activation email resent.');
              setCoachToResend(null);
              setIsSendingActivation(false);
            },
            onError: () => {
              setIsSendingActivation(false);
            },
          }));
        }}
        title='Resend Activation'
        message={usernamesEnabled
          ? 'Registration emails cannot be sent when usernames are enabled. To give this coach access, reset their password and provide it to them directly.'
          : 'A new random password will be generated and sent to the selected coach.'}
        cancelLabel={usernamesEnabled ? 'Close' : 'Cancel'}
        confirmLabel='Resend'
        confirmIcon='send'
        destructive={false}
        isLoading={isSendingActivation}
        hideConfirm={usernamesEnabled}
      >
        <InfoWrapper>
          <Text styledTextProps={{ fontSize: '14px' }}>
            {usernamesEnabled
              ? `${displayResend?.first} ${displayResend?.last}`
              : `Send to: ${displayResend?.first} ${displayResend?.last}`}
          </Text>
        </InfoWrapper>
      </ConfirmModal>

      {/* Delete Individual Coach Modal */}
      <ConfirmModal
        isOpen={!!coachToDelete}
        onClose={() => setCoachToDelete(null)}
        onConfirm={() => dispatch(deleteUsers(accountCode, [coachToDelete.id], {
          onSuccess: () => {
            showSuccessToast(`${coachToDelete.first} ${coachToDelete.last} has been deleted.`);
            refetchCoaches();
            setCoachToDelete(null);
          },
        }))}
        title='Delete Coach'
        message={`Are you sure you want to delete ${displayDelete?.first} ${displayDelete?.last}? This action cannot be undone.`}
        isLoading={isDeletingUsers}
      />

      {/* Bulk Delete Modal */}
      <ConfirmModal
        isOpen={bulkAction === 'deleteCoaches'}
        onClose={() => { setBulkAction(null); setBulkSelectedIds([]); }}
        onConfirm={confirmBulkDelete}
        title='Delete Coaches'
        message={`Are you sure you want to delete ${bulkSelectedIds.length} coach${bulkSelectedIds.length === 1 ? '' : 'es'}? This action cannot be undone.`}
        isLoading={isDeletingUsers}
      />

      <ExportDataModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        onExportXLSX={exportToXLSX}
        onExportXLS={exportToXLS}
        onExportCSV={exportToCSV}
      />

      <AddCoachModal
        isOpen={isAddCoachOpen}
        onClose={() => setIsAddCoachOpen(false)}
        onCreated={refetchCoaches}
      />

      <AddCoachModal
        isOpen={!!coachToEdit}
        onClose={() => setCoachToEdit(null)}
        coach={coachToEdit}
        onUpdated={refetchCoaches}
      />

      <HiddenTable id='coaches-export-table'>
        <thead>
          <tr>
            <th>First</th>
            <th>Last</th>
            <th>{usernamesEnabled ? 'Username' : 'Email'}</th>
            <th>Groups</th>
            <th>Calendar</th>
            <th>Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {visibleCoaches.map((c) => (
            <tr key={c.id}>
              <td>{c.first || ''}</td>
              <td>{c.last || ''}</td>
              <td>{(usernamesEnabled ? c.username : c.email) || ''}</td>
              <td>{getCoachGroupNames(c)}</td>
              <td>{getCoachCalendarNames(c)}</td>
              <td>{getCoachLastActivity(c)}</td>
            </tr>
          ))}
        </tbody>
      </HiddenTable>
    </Container>
  );
};

ManageCoaches.propTypes = {
  backPath: PropTypes.string,
  canManageCoaches: PropTypes.bool,
};

ManageCoaches.defaultProps = {
  backPath: '/dashboard/manage',
  canManageCoaches: true,
};

export default ManageCoaches;
